'use client';

/**
 * Live portfolio → lobby "Your Seats" / profile holdings shape.
 * Mirrors `groupSeatsByCoin` + USD helpers used in the main header.
 */

import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatUnits } from 'viem';
import { useWallet } from '@/hooks/use-wallet';
import { useEthPrice } from '@/hooks/use-eth-price';
import { useTakeoverPrice } from '@/hooks/use-takeover-price';
import { effectiveTokenAddress, useTokenPrices } from '@/hooks/use-token-prices';
import { groupSeatsByCoin, type RawSeatData } from '@/components/seats/seat-utils';
import type { CoinSeatsData, SeatPosition } from '@/components/seats/coin-seats-card';
import { queryKeys } from '@/lib/query-keys';
import { fetchPortfolioJson } from '@/lib/fetchers/portfolio-json';
import { fetchPendingFeesCached } from '@/lib/fetchers/pending-fees-query';
import {
  getTaxTokenConfig,
  mergePortfolioPreservingCachedSeats,
} from '@/lib/drawer-utils';
import { CONSTANTS } from '@/lib/types';
import { safeBigInt } from '@/lib/bigint';

export type LobbySeatLockRisk = 'safe' | 'warn' | 'danger';

export interface LobbySeatRow {
  id: string;
  /**
   * Authoritative 0-indexed contract seat index — every UGM call
   * (`setPrice`, `addDeposit`, `withdrawDeposit`, …) takes the raw
   * uint256 seat id, so we expose it explicitly rather than parsing
   * it back out of the human-friendly `seatCode` label below.
   */
  seatIndex: number;
  /**
   * 1-indexed, zero-padded label for display (e.g. "001", "042").
   * Mirrors what the board surface paints on every tile via
   * `displaySeat.seatIndex + 1`, so the manage modal stops reading
   * one off ("Tile #0" while the board chip says "1") for users
   * cross-referencing rows.
   */
  seatCode: string;
  price: number;
  deposit: number;
  /** Approximate days of tax runway from on-chain balances (live). */
  runwayDays: number;
  /** Weekly tax in USD for bulk price/deposit preview math. */
  weeklyTaxUsd: number;
  rewardsAccrued: number;
  lockRisk: LobbySeatLockRisk;
  /**
   * Tax-depleted seat ("zombie") — onchain holder is still the viewer
   * but the deposit has run out, so a `pokeTax` from anyone forfeits it.
   * The lobby header tiles chip counts these (it keys off the indexer's
   * `isActive` flag); the manage modal surfaces them with this flag so
   * the row count matches and the user can top-up or abandon.
   */
  isForfeited?: boolean;
  /**
   * Raw onchain price + balance carried forward so the new
   * cross-board "My Tiles" view can render exact figures (and feed
   * them straight back into UGM call builders) without round-tripping
   * through the lossy USD `price` / `deposit` numbers above. The
   * existing single-board manage modal also avoided lossy display
   * helpers — these are the same values it pulled per-board via
   * `groups[].seats[].selfAssessedPrice` etc., now exposed per row
   * so the multi-board view doesn't have to thread `groups` alongside
   * the flat row list.
   */
  rawPrice: bigint;
  rawBalance: bigint;
  rawWeeklyTax: bigint;
}

export interface LobbyBoardHolding {
  boardId: string;
  boardName: string;
  boardImageUrl?: string | null;
  /** Underlying coin symbol (e.g. `USDC`, `DEGEN`). Used for header
   *  labels in the My Tiles view ("Tiles on DEGEN board"). */
  boardSymbol?: string;
  seatCount: number;
  tileValue: number;
  /** True when the underlying pool is on the V2 UnifiedGridManager. V1
   *  (legacy) grids still exist in the portfolio but are managed from the
   *  classic app — the lobby drawer filters them out and surfaces a link. */
  isV2: boolean;
  /** UGM contract that backs this grid. Required by every tile-action
   *  call builder, so we hoist it to the row level. Lower-cased. */
  managerAddress?: string;
  /** Tax token paid against this board (USDC or TAKEOVER, today). All
   *  rows belonging to the same board share the same tax token; the
   *  My Tiles view groups bulk-deposit inputs by token using this. */
  taxToken?: string;
  taxTokenDecimals?: number;
  /** "USDC" | "TAKEOVER" derived once per board. Cheaper than calling
   *  `getTaxTokenConfig` for every row. */
  taxTokenSymbol: 'USDC' | 'TAKEOVER';
  /** Optional logo URI for the tax token. Surfaced inline next to
   *  every seat row in the new "My Tiles" view so the user can tell
   *  at a glance which token they'll be depositing. */
  taxTokenLogoUrl?: string | null;
  seats: LobbySeatRow[];
}

interface PortfolioResponse {
  data?: { seats?: RawSeatData[] };
}

function runwayToRisk(runwaySeconds: number): LobbySeatLockRisk {
  if (runwaySeconds < CONSTANTS.RUNWAY_CRITICAL) return 'danger';
  if (runwaySeconds < CONSTANTS.RUNWAY_HEALTHY) return 'warn';
  return 'safe';
}

function selfAssessedToUsd(
  group: CoinSeatsData,
  price: bigint,
  takeoverPriceUsd: number,
): number {
  const isV2 = !!group.isV2Grid;
  if (!isV2) {
    return Number(price) / 1e6;
  }
  const ttc = getTaxTokenConfig(group.taxToken, undefined, group.taxTokenDecimals);
  if (ttc.isTakeover && takeoverPriceUsd > 0) {
    return parseFloat(formatUnits(price, ttc.decimals)) * takeoverPriceUsd;
  }
  return Number(price) / 1e6;
}

function balanceToUsd(
  group: CoinSeatsData,
  balance: bigint,
  takeoverPriceUsd: number,
): number {
  const ttc = getTaxTokenConfig(group.taxToken, undefined, group.taxTokenDecimals);
  if (ttc.isTakeover && takeoverPriceUsd > 0) {
    return parseFloat(formatUnits(balance, ttc.decimals)) * takeoverPriceUsd;
  }
  return Number(balance) / 1e6;
}

function buildClaimableByPool(
  v2Breakdown: { gridId: string; usdValue: number | null }[],
  v1PendingEth: bigint,
  groups: CoinSeatsData[],
  ethPrice: number,
): Map<string, number> {
  const map = new Map<string, number>();
  for (const row of v2Breakdown) {
    const v = row.usdValue;
    if (row.gridId && typeof v === 'number' && v > 0) {
      map.set(row.gridId, v);
    }
  }
  const v1Eth = Number(v1PendingEth) / 1e18;
  const v1Usd = ethPrice > 0 && v1Eth > 0 ? v1Eth * ethPrice : 0;
  const v1Groups = groups.filter((g) => !g.isV2Grid);
  const v1Seats = v1Groups.reduce((n, g) => n + g.seats.length, 0);
  if (v1Usd > 0 && v1Seats > 0) {
    const perSeat = v1Usd / v1Seats;
    for (const g of v1Groups) {
      const prev = map.get(g.poolId) ?? 0;
      map.set(g.poolId, prev + perSeat * g.seats.length);
    }
  }
  return map;
}

function holdingFromGroup(
  group: CoinSeatsData,
  takeoverPriceUsd: number,
  claimableByPool: Map<string, number>,
): LobbyBoardHolding {
  const poolClaimable = claimableByPool.get(group.poolId) ?? 0;
  const perSeatReward =
    group.seats.length > 0 ? poolClaimable / group.seats.length : 0;

  const seats: LobbySeatRow[] = group.seats.map((s: SeatPosition) => {
    const price = selfAssessedToUsd(group, s.selfAssessedPrice, takeoverPriceUsd);
    const deposit = balanceToUsd(group, s.balance, takeoverPriceUsd);
    const runwayDays = Math.max(0, s.runwaySeconds / 86400);
    const weeklyTaxUsd = balanceToUsd(group, s.weeklyTax, takeoverPriceUsd);
    return {
      id: `${group.poolId}-${s.seatIndex}`,
      seatIndex: s.seatIndex,
      seatCode: String(s.seatIndex + 1).padStart(3, '0'),
      price,
      deposit,
      runwayDays,
      weeklyTaxUsd,
      rewardsAccrued: perSeatReward,
      // Forfeited rows get the strongest risk treatment regardless of the
      // computed runway (which is 0 anyway) so they sort to the top of
      // any "fix the problem first" view in the modal.
      lockRisk: s.isForfeited ? 'danger' : runwayToRisk(s.runwaySeconds),
      isForfeited: s.isForfeited,
      rawPrice: s.selfAssessedPrice,
      rawBalance: s.balance,
      rawWeeklyTax: s.weeklyTax,
    };
  });

  const tileValue = seats.reduce((a, x) => a + x.price, 0);
  const ttc = getTaxTokenConfig(group.taxToken, undefined, group.taxTokenDecimals);

  return {
    boardId: group.poolId,
    boardName: group.poolName,
    boardImageUrl: group.poolImageUrl ?? null,
    boardSymbol: group.poolSymbol,
    seatCount: seats.length,
    tileValue,
    isV2: !!group.isV2Grid,
    managerAddress: group.managerAddress?.toLowerCase(),
    taxToken: group.taxToken,
    taxTokenDecimals: group.taxTokenDecimals,
    taxTokenSymbol: ttc.isTakeover ? 'TAKEOVER' : 'USDC',
    // Reuse the board's coin imageUrl as a cheap fallback when the
    // tax token is the board's own coin (TAKEOVER boards) or use the
    // canonical USDC mark when the tax token is USDC. `imageUrl` is
    // already populated in the indexer payload and the hook upstream.
    taxTokenLogoUrl: ttc.isTakeover ? (group.poolImageUrl ?? null) : USDC_LOGO_URL,
    seats,
  };
}

// USDC's canonical brand mark. Embedded as a constant here (rather
// than threading it through env vars) because the same image is
// sourced from coingecko on every other surface; we centralise the
// URL to avoid inconsistent USDC logos across the app.
const USDC_LOGO_URL =
  'https://assets.coingecko.com/coins/images/6319/small/usdc.png';

export function useLobbySeatHoldings() {
  const { address, isConnected } = useWallet();
  const canFetch = isConnected && !!address;
  const { ethPrice } = useEthPrice();
  const { priceUsd: takeoverPriceUsd } = useTakeoverPrice();
  const queryClient = useQueryClient();

  const { data: portfolio, isLoading: portfolioLoading } = useQuery({
    queryKey: queryKeys.portfolio(address),
    queryFn: async () => {
      if (!address) return {} as PortfolioResponse;
      const fresh = await fetchPortfolioJson(address);
      // The portfolio fetch hits an indexer that can lag the chain by a
      // few seconds after a transaction. `refreshAfterTransaction` writes
      // freshly RPC-read seat values into this same cache via
      // `updatePortfolioSeatsInCache` — without this merge a refetch
      // landing during that lag window would stomp the optimistic
      // values and the manage modal would briefly flicker back to the
      // pre-tx state. Mirrors the pattern in `useGridPage`'s queryFn.
      const cached = queryClient.getQueryData(queryKeys.portfolio(address));
      const merged = mergePortfolioPreservingCachedSeats(cached, fresh);
      return (merged ?? {}) as PortfolioResponse;
    },
    enabled: canFetch,
    staleTime: 30_000,
  });

  const { data: pendingFees, isLoading: feesLoading } = useQuery({
    queryKey: queryKeys.pendingFees(address),
    queryFn: async () => {
      if (!address) return null;
      try {
        return await fetchPendingFeesCached(address);
      } catch {
        return null;
      }
    },
    enabled: canFetch,
    staleTime: 25_000,
  });

  // Drop primary-mint creator seats — when the viewer is the grid's
  // creator, `getGridSeatsByHolder` returns every unsold seat they hold
  // for that grid. Those seats carry zero deposit (creators are exempt
  // from Harberger tax on their own primary-mint inventory) so the
  // forfeit-runway projection treats them as zombies and the manage
  // modal would surface red "Forfeit pending" warnings for tiles that
  // genuinely can't be poked. The board's `MySeatsCard` already hides
  // them with `!s.isCreatorSeat`; do the same here so "Your Tiles"
  // matches across surfaces. Post-graduation creator seats lose their
  // exemption (they pay tax like everyone else), so we only filter
  // pre-graduation — same condition `use-grid-page.helpers.ts` uses.
  const rawSeats = useMemo(() => {
    // Hard gate on the connected state. Without this guard, the
    // global `placeholderData: (prev) => prev` default in
    // `query-provider.tsx` keeps the previous wallet's portfolio
    // payload alive on the disconnected observer — even after
    // `handleLogout` calls `queryClient.clear()` — because
    // `clear()` only wipes the cache, not each observer's local
    // placeholder state. The visible symptom was: disconnect on
    // mobile and the "My boards" rail in `MobileBoardRail` keeps
    // rendering the previous user's holdings until a hard reload.
    // Short-circuiting at the data layer means consumers
    // (`MobileBoardRail`, `TileManagementModal`,
    // `useLobbyHeaderStats`) all snap to "no holdings" the
    // instant `address` flips, regardless of cache mechanics.
    if (!canFetch || !address) return [];
    const all = portfolio?.data?.seats ?? [];
    const viewerLower = address.toLowerCase();
    return all.filter((seat) => {
      const creator = seat.pool?.creatorAddress?.toLowerCase();
      if (!creator || creator !== viewerLower) return true;
      if (seat.pool?.isGraduated) return true;
      // `safeBigInt` here matters specifically on mobile: this hook
      // backs `MobileBoardRail` (mounted in `AppShell.MobileChromeTop`,
      // which sits *above* the lobby's `error.tsx` boundary). A bare
      // `BigInt()` throw here would tear down the entire mobile chrome
      // before the route-group boundary can catch it.
      const deposit = safeBigInt(seat.balance);
      return deposit > 0n;
    });
  }, [portfolio, address, canFetch]);

  const priceAddrs = useMemo(() => {
    const s = new Set<string>();
    for (const seat of rawSeats) {
      if (seat.pool?.isV2Grid && seat.pool?.v2YieldToken) {
        s.add(effectiveTokenAddress(seat.pool.v2YieldToken));
      }
    }
    return [...s];
  }, [rawSeats]);

  const { prices: tokenPrices } = useTokenPrices(priceAddrs);

  const grouped = useMemo(() => {
    if (rawSeats.length === 0) return [];
    return groupSeatsByCoin(
      rawSeats,
      undefined,
      ethPrice,
      tokenPrices,
      undefined,
      // Lobby manage modal surfaces zombie seats so its row count tracks
      // the header chip's `isActive` count — see `LobbySeatRow.isForfeited`
      // for the full reasoning.
      { includeForfeited: true },
    );
  }, [rawSeats, ethPrice, tokenPrices]);

  const holdings = useMemo((): LobbyBoardHolding[] => {
    if (grouped.length === 0) return [];
    const claimableByPool = buildClaimableByPool(
      pendingFees?.v2Breakdown ?? [],
      pendingFees?.v1PendingEth ?? 0n,
      grouped,
      ethPrice,
    );
    const tp = takeoverPriceUsd || 0;
    return grouped.map((g) => holdingFromGroup(g, tp, claimableByPool));
  }, [grouped, pendingFees, takeoverPriceUsd, ethPrice]);

  const totalTileUsd = useMemo(
    () => holdings.reduce((a, h) => a + h.tileValue, 0),
    [holdings],
  );

  return {
    holdings,
    /** Raw per-coin groups keyed by poolId. Callers drilling into a specific
     *  board use this to build `SeatData[]` for the main bulk-action drawer
     *  — the display-shape `holdings` loses bigint precision. */
    groups: grouped,
    isLoading: portfolioLoading || feesLoading,
    totalTileUsd,
    boardCount: holdings.length,
  };
}

/**
 * Flat row shape used by the cross-board "My Tiles" table. Each row
 * carries every field the table needs to render and every field the
 * SDK call builders need to dispatch a transaction — including the
 * board the seat lives on, the manager address, and the tax-token
 * metadata — so consumers don't have to re-join `holdings[]` with
 * `groups[]` per render.
 */
export interface FlatTileRow extends LobbySeatRow {
  boardId: string;
  boardName: string;
  boardSymbol?: string;
  boardImageUrl?: string | null;
  isV2: boolean;
  managerAddress?: string;
  taxToken?: string;
  taxTokenDecimals?: number;
  taxTokenSymbol: 'USDC' | 'TAKEOVER';
  taxTokenLogoUrl?: string | null;
}

/**
 * Project `LobbyBoardHolding[]` into a flat list of seat rows with
 * board metadata embedded. Excludes legacy V1 seats — those still
 * route through the classic app and the new view links out to it.
 *
 * Pure helper (no React) so it composes with `useMemo` upstream.
 */
export function flattenTileRows(holdings: LobbyBoardHolding[]): FlatTileRow[] {
  const rows: FlatTileRow[] = [];
  for (const h of holdings) {
    if (!h.isV2) continue;
    for (const s of h.seats) {
      rows.push({
        ...s,
        boardId: h.boardId,
        boardName: h.boardName,
        boardSymbol: h.boardSymbol,
        boardImageUrl: h.boardImageUrl,
        isV2: h.isV2,
        managerAddress: h.managerAddress,
        taxToken: h.taxToken,
        taxTokenDecimals: h.taxTokenDecimals,
        taxTokenSymbol: h.taxTokenSymbol,
        taxTokenLogoUrl: h.taxTokenLogoUrl,
      });
    }
  }
  return rows;
}

/** @deprecated Demo offline snapshot — connected lobby uses `useLobbyHeaderStats`. */
export function getOfflineDemoTileTotals(): { total: number; coinSymbol: string } {
  return { total: 0, coinSymbol: 'USD' };
}
